/**
 * assignedWork — the three requests behind a person's Matters, Projects and
 * Tasks tabs, and the row readings the tables need.
 *
 * ONE CONTRACT, THREE DOMAINS (`feature/final-revamp-backend`, 61eb5b1):
 *
 *   POST /matters/get_assigned_matters   → { matters:  [...], count }
 *   POST /tasks/get_assigned_tasks       → { tasks:    [...], count }
 *   POST /projects/get_assigned_projects → { projects: [...], count }
 *
 * Body: `user_uuid` (alias `UserUUID`), with `user_email` (alias `UserEmail`)
 * as the legacy external-collaborator path. The backend prefers the uuid and
 * only falls back to the email when it is absent, so sending both costs one
 * field and covers a record whose `user_info.id` never arrived. Omitting BOTH
 * returns the CALLER's own work — the one answer a person page must never
 * give, which is why the target is built in exactly one place here.
 *
 * WHY THE MATTERS TAB WAS EMPTY. The page posted `/cases/get_assigned_cases`,
 * and that route has never existed: the matters blueprint has always been
 * `/matters`, and its older handler keyed on `UserEmail`. So the tab could
 * only ever have been empty or an error.
 *
 * NONE OF THE THREE PAGINATE. Each returns the whole list plus `count`, so
 * there is no `page`/`limit` to send and no "load more" to offer — a page
 * parameter the server ignores is a promise the UI cannot keep.
 *
 * They live here rather than in `EmployeeDetail` so the contract can be
 * pinned by a test (`assignedWork.test.ts`) without rendering the page, and
 * so the hook that calls them can memoise on a stable function identity.
 */

import apis from '../../../../utils/apis';

export interface AssignedResult<T = any> {
  rows: T[];
  count: number;
}

/** Whose work to return. The same object on all three bodies. */
export const assignedTarget = (uuid: string, email: string) => ({
  user_uuid: uuid || undefined,
  user_email: email || undefined,
});

export const requestAssignedMatters = async (
  uuid: string,
  email: string
): Promise<AssignedResult> => {
  const resp = await apis.post('/matters/get_assigned_matters', {
    ...assignedTarget(uuid, email),
    // The default, stated: a person's page lists what they are working on,
    // not what has been put away.
    archived: 'exclude',
  });
  const rows = resp.data?.matters || [];
  return { rows, count: resp.data?.count ?? rows.length };
};

export const requestAssignedTasks = async (
  uuid: string,
  email: string
): Promise<AssignedResult> => {
  const resp = await apis.post('/tasks/get_assigned_tasks', assignedTarget(uuid, email));
  const rows = resp.data?.tasks || [];
  return { rows, count: resp.data?.count ?? rows.length };
};

export const requestAssignedProjects = async (
  uuid: string,
  email: string
): Promise<AssignedResult> => {
  const resp = await apis.post('/projects/get_assigned_projects', assignedTarget(uuid, email));
  const rows = resp.data?.projects || [];
  return { rows, count: resp.data?.count ?? rows.length };
};

/**
 * The client on a matter row, for the list's one-line cell.
 *
 * `get_assigned_matters` enriches each matter with `matter_clients`
 * (`{ count, clients: [{ name, … }] }`) — a matter can have several, so the
 * cell names the first and counts the rest rather than silently showing one
 * of many.
 */
export const clientNameOf = (matter: any): string => {
  const clients: any[] = matter?.matter_clients?.clients || [];
  if (clients.length === 0) return '';
  const [first, ...rest] = clients;
  const name = first?.name || '';
  return rest.length > 0 ? `${name} +${rest.length}` : name;
};
